import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import NotificationBell from './NotificationBell';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { Menu, X, Home, ClipboardList, BookOpen, Calendar, FileText, Settings, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TooltipProvider } from '@/components/ui/tooltip';

/**
 * User Dashboard Wrapper with consistent navigation
 * Used for all authenticated participant pages
 */
function UserDashboardWrapper({ children }) {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [coreCompleted, setCoreCompleted] = useState(false);

  useEffect(() => {
    // Check if CORE diagnostic is completed
    axios.get('/api/assessments').then(res => {
      const assessments = res.data.assessments || [];
      const core = assessments.find(a => a.type === 'archetype' && a.status === 'completed');
      setCoreCompleted(!!core);
    }).catch(() => {});
  }, []);

  const navItems = [
    { href: '/dashboard/home', label: 'Dashboard', icon: Home },
    { href: '/dashboard/assessments', label: 'Diagnostics', icon: ClipboardList },
    { href: '/library', label: 'Library', icon: BookOpen },
    { href: '/book', label: 'Book Session', icon: Calendar, requiresCore: true },
    { href: '/apply', label: 'Apply for PATH', icon: FileText },
  ];

  const isActive = (href) => {
    const path = window.location.pathname;
    if (href === '/dashboard/home') {
      return path === '/dashboard' || path === '/dashboard/home';
    }
    return path === href || path.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <a href="/" className="flex items-center" data-testid="user-logo">
                <Logo variant="full" size="default" />
              </a>
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const locked = item.requiresCore && !coreCompleted;
                  if (locked) {
                    return (
                      <span
                        key={item.href}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-muted-foreground/50 cursor-not-allowed"
                        title="Complete your CORE Diagnostic to unlock"
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <Lock className="w-3.5 h-3.5" />
                        {item.label}
                      </span>
                    );
                  }
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </a>
                  );
                })}
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <NotificationBell />
              <a
                href="/dashboard/settings"
                className="p-2 text-muted-foreground hover:text-foreground transition-smooth rounded-md hover:bg-muted hidden sm:flex"
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5" />
              </a>
              <span className="text-sm text-muted-foreground px-2 hidden lg:inline">
                {user?.firstName || user?.email}
              </span>
              <button
                onClick={logout}
                className="text-sm text-muted-foreground hover:text-foreground transition-smooth px-2 hidden sm:block"
                data-testid="logout-button"
              >
                Logout
              </button>
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-muted-foreground hover:text-foreground"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border mt-4 pt-4 pb-2">
              <nav className="flex flex-col gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const locked = item.requiresCore && !coreCompleted;
                  if (locked) {
                    return (
                      <span key={item.href} className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground/50 cursor-not-allowed">
                        <Lock className="w-3.5 h-3.5" /> {item.label}
                      </span>
                    );
                  }
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                        active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </a>
                  );
                })}
                <div className="border-t border-border my-2" />
                <a
                  href="/dashboard/settings"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted rounded-md"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </a>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted rounded-md text-left w-full"
                >
                  Logout
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-8">
        {children}
      </main>
    </div>
  );
}

export default UserDashboardWrapper;
